import { gql } from '@apollo/client';
import { getApolloClient } from '@faustwp/core';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { SEO, SchemaMarkup } from '../components';
import ArchiveTjenesteTemplate from '../wp-templates/archive-tjeneste';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { NavigationMenu } from '../components';
import { TjenesteArchiveSEOFragment } from '../fragments/tjenester/TjenesteArchiveSEOFragment';
import * as MENUS from '../constants/menus';
import { createSchema, createWebPageSchema, createBreadcrumbSchema } from '../lib/schema';
import { buildSiteUrl } from '../lib/polylang';

const GET_TJENESTER_ARCHIVE = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${TjenesteArchiveSEOFragment}
  query GetTjenesterArchive(
    $headerLocation: MenuLocationEnum,
    $footerLocation: MenuLocationEnum
  ) {
    tjenester(first: 100, where: {taxQuery: {taxArray: {terms: "4", field: TAXONOMY_ID}}}) {
      nodes {
        id
        databaseId
        ... on Tjeneste {
          title
          slug
          uri
          terms {
            nodes {
              id
              name
            }
          }
          parent {
            node {
              ... on Tjeneste {
                id
                title
                uri
              }
            }
          }
          children {
            nodes {
              ... on Tjeneste {
                id
                title
                uri
              }
            }
          }
          tjenesterPost {
            serviceDescription
          }
        }
      }
    }
    contentType(id: "tjeneste", idType: NAME) {
      ...TjenesteArchiveSEOFragment
    }
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
         headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
       nodes {
         ...NavigationMenuItemFragment
       }
     }
     footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
       nodes {
         ...NavigationMenuItemFragment
       }
     }
  }
`;

export default function TjenesterArchive(props) {
  const router = useRouter();
  
  // Extract data from props
  const { data } = { data: props.data || {} };
  const tjenester = data?.tjenester?.nodes ?? [];
  const archiveSEO = data?.contentType?.seo;
  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;

  // SEO values
  const pageTitle = archiveSEO?.title || 'Dette kan vi';
  const pageDescription = archiveSEO?.description || 'Vi tilbyr skreddersydde digitale løsninger som hjelper din bedrift med å vokse og lykkes i den digitale verden.';
  const siteName = siteTitle || 'Mediabooster';
  const finalTitle = pageTitle?.includes(siteName) ? pageTitle : `${pageTitle} - ${siteName}`;
  
  // Get social image from WordPress SEO or use fallback
  const socialImage = archiveSEO?.openGraph?.image?.url || 
                     archiveSEO?.openGraph?.image?.secureUrl || 
                     'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';

  const faviconUrl = data?.generalSettings?.favicon?.node?.mediaItemUrl || 
                     data?.mediaboosterSettings?.mediaboosterSettingsFields?.favicon?.node?.mediaItemUrl;

  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no'}/tjenester`;

  // Generate schema for services archive page
  const pageSchema = useMemo(() => {
    const breadcrumbs = [
      { label: 'Hjem', path: '/' },
      { label: 'Tjenester', path: '/tjenester' }
    ];

    // Create ItemList schema for services
    const serviceItems = tjenester
      .filter(t => !t.parent?.node) // Only top-level services
      .map((service, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': service.title,
        'url': `https://mediabooster.no${service.uri}`
      }));

    return createSchema([
      createWebPageSchema({
        title: finalTitle,
        description: pageDescription,
        url: '/tjenester'
      }),
      {
        '@type': 'CollectionPage',
        '@id': 'https://mediabooster.no/tjenester/#collectionpage',
        'name': finalTitle,
        'description': pageDescription,
        'url': 'https://mediabooster.no/tjenester',
        'mainEntity': {
          '@type': 'ItemList',
          'itemListElement': serviceItems,
          'numberOfItems': serviceItems.length
        }
      },
      createBreadcrumbSchema(breadcrumbs)
    ]);
  }, [finalTitle, pageDescription, tjenester]);

  return (
    <>
      <SEO
        title={finalTitle}
        description={pageDescription}
        imageUrl={socialImage}
        url={canonicalUrl}
        faviconUrl={faviconUrl}
        siteName={siteName}
        ogType="website"
        locale="nb_NO"
        hreflangAlternates={[
          { hrefLang: 'no', href: canonicalUrl },
          { hrefLang: 'en', href: buildSiteUrl('/en/services') },
          { hrefLang: 'x-default', href: canonicalUrl },
        ]}
      />
      <SchemaMarkup schema={pageSchema} />
      <ArchiveTjenesteTemplate {...props} suppressLayoutSeo hideCtaButtons />
    </>
  );
}

export async function getStaticProps() {
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: GET_TJENESTER_ARCHIVE,
      variables: {
        headerLocation: MENUS.PRIMARY_LOCATION,
        footerLocation: MENUS.FOOTER_LOCATION,
      },
      fetchPolicy: 'no-cache'
    });
    
    // Fetch shared data using exact same queries as components
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        query GetSharedTjenesterData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # Partners data for NavigationMenu
          partnere(first: 100, where: { language: NB }) {
            nodes {
              id
              title
              slug
              uri
            }
          }
          
          # MediaBooster settings - includes Footer data and ContactCTA data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              logo {
                node {
                  mediaItemUrl
                  altText
                }
              }
              logoIcon {
                node {
                  mediaItemUrl
                  altText
                }
              }
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
              ctaSeksjonMotebooking {
                heading
                subtext
              }
            }
          }
          
          # Frontpage tjenesteboks data used by Header
          page(id: "14", idType: DATABASE_ID) {
            frontPage {
              tjenesteboks {
                tjenestenavn
                underoverskrift
                link
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: pageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching tjenester archive data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
} 